import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Placeholder from "@tiptap/extension-placeholder";
import { Button, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { IDocumentTemplateVariable } from "../../shared/interfaces/document-template/IDocumentTemplate";

// ---------------------------------------------------------------------------
// Styles – injected once into <head> (no separate CSS file needed)
// ---------------------------------------------------------------------------
const EDITOR_CSS = `
.rt-editor-wrapper {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  overflow: hidden;
}
.rt-editor-wrapper:focus-within {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13,110,253,.25);
}
.rt-toolbar {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 6px 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  align-items: center;
}
.rt-toolbar button {
  background: none;
  border: none;
  border-radius: 4px;
  padding: 3px 7px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1.5;
  color: #495057;
  transition: background 0.1s;
}
.rt-toolbar button:hover {
  background: #e9ecef;
}
.rt-toolbar button.is-active {
  background: #dee2e6;
  color: #0d6efd;
}
.rt-toolbar .separator {
  width: 1px;
  height: 22px;
  background: #dee2e6;
  margin: 0 4px;
}
.rt-editor-content .ProseMirror {
  min-height: 200px;
  padding: 12px 14px;
  outline: none;
  font-size: 0.9rem;
}
.rt-editor-content .ProseMirror p.is-editor-empty:first-child::before {
  color: #adb5bd;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}
.rt-variable-chip {
  display: inline-block;
  background: #e7f1ff;
  border: 1px solid #b6d4fe;
  border-radius: 12px;
  padding: 0 8px;
  font-size: 0.78rem;
  font-family: monospace;
  color: #0a58ca;
  white-space: nowrap;
  cursor: default;
}
`;

let styleInjected = false;
function injectStyles() {
    if (styleInjected) return;
    styleInjected = true;
    const tag = document.createElement("style");
    tag.textContent = EDITOR_CSS;
    document.head.appendChild(tag);
}

// ---------------------------------------------------------------------------
// Toolbar button helpers
// ---------------------------------------------------------------------------
interface ToolbarBtnProps {
    onClick: () => void;
    isActive?: boolean;
    title: string;
    children: React.ReactNode;
    disabled?: boolean;
}

const ToolbarBtn = ({ onClick, isActive, title, children, disabled }: ToolbarBtnProps) => (
    <OverlayTrigger placement="top" overlay={<Tooltip>{title}</Tooltip>}>
        <button
            type="button"
            className={isActive ? "is-active" : ""}
            onClick={onClick}
            disabled={disabled}
        >
            {children}
        </button>
    </OverlayTrigger>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
interface RichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    variables?: IDocumentTemplateVariable[];
    onFetchVariables?: () => void;
    placeholder?: string;
}

const RichTextEditor = ({
    value,
    onChange,
    variables = [],
    onFetchVariables,
    placeholder = "Write your section content here...",
}: RichTextEditorProps) => {
    const [rawMode, setRawMode] = useState(false);
    const [rawValue, setRawValue] = useState(value);
    const lastValueRef = useRef(value);

    useEffect(() => {
        injectStyles();
    }, []);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextStyle,
            Color,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            Placeholder.configure({ placeholder }),
        ],
        content: value,
        onUpdate: ({ editor }: { editor: any }) => {
            const html = editor.getHTML();
            lastValueRef.current = html;
            onChange(html);
        },
    });

    // Sync external value into editor when it changes from outside
    useEffect(() => {
        if (!editor) return;
        if (value !== lastValueRef.current) {
            lastValueRef.current = value;
            editor.commands.setContent(value);
            setRawValue(value);
        }
    }, [value, editor]);

    // Toggle between visual / raw HTML
    const handleToggleRaw = () => {
        if (!rawMode) {
            // going to raw — capture latest HTML
            setRawValue(editor?.getHTML() ?? value);
        } else {
            // coming back from raw — push raw into editor
            editor?.commands.setContent(rawValue);
            lastValueRef.current = rawValue;
            onChange(rawValue);
        }
        setRawMode((prev) => !prev);
    };

    const handleRawChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setRawValue(e.target.value);
        onChange(e.target.value);
    };

    const insertVariable = (variable: string) => {
        if (rawMode) {
            // Insert at cursor in textarea (best effort)
            onChange(rawValue + variable);
            setRawValue((prev) => prev + variable);
            return;
        }
        if (!editor) return;
        editor.chain().focus().insertContent(variable).run();
    };

    // Fetch variables on first open of the dropdown
    const handleVariableDropdownToggle = (isOpen: boolean) => {
        if (isOpen && variables.length === 0) {
            onFetchVariables?.();
        }
    };

    return (
        <div className="rt-editor-wrapper">
            {/* Toolbar */}
            <div className="rt-toolbar">
                {!rawMode && editor && (
                    <>
                        {/* Text style */}
                        <ToolbarBtn
                            title="Bold"
                            onClick={() => editor.chain().focus().toggleBold().run()}
                            isActive={editor.isActive("bold")}
                        >
                            <strong>B</strong>
                        </ToolbarBtn>
                        <ToolbarBtn
                            title="Italic"
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                            isActive={editor.isActive("italic")}
                        >
                            <em>I</em>
                        </ToolbarBtn>
                        <ToolbarBtn
                            title="Underline"
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                            isActive={editor.isActive("underline")}
                        >
                            <span style={{ textDecoration: "underline" }}>U</span>
                        </ToolbarBtn>

                        <div className="separator" />

                        {/* Headings */}
                        <ToolbarBtn
                            title="Heading 2"
                            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                            isActive={editor.isActive("heading", { level: 2 })}
                        >
                            H2
                        </ToolbarBtn>
                        <ToolbarBtn
                            title="Heading 3"
                            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                            isActive={editor.isActive("heading", { level: 3 })}
                        >
                            H3
                        </ToolbarBtn>

                        <div className="separator" />

                        {/* Lists */}
                        <ToolbarBtn
                            title="Bullet list"
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                            isActive={editor.isActive("bulletList")}
                        >
                            <i className="ti ti-list" />
                        </ToolbarBtn>
                        <ToolbarBtn
                            title="Ordered list"
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                            isActive={editor.isActive("orderedList")}
                        >
                            <i className="ti ti-list-numbers" />
                        </ToolbarBtn>

                        <div className="separator" />

                        {/* Alignment */}
                        <ToolbarBtn
                            title="Align left"
                            onClick={() => editor.chain().focus().setTextAlign("left").run()}
                            isActive={editor.isActive({ textAlign: "left" })}
                        >
                            <i className="ti ti-align-left" />
                        </ToolbarBtn>
                        <ToolbarBtn
                            title="Align center"
                            onClick={() => editor.chain().focus().setTextAlign("center").run()}
                            isActive={editor.isActive({ textAlign: "center" })}
                        >
                            <i className="ti ti-align-center" />
                        </ToolbarBtn>
                        <ToolbarBtn
                            title="Align right"
                            onClick={() => editor.chain().focus().setTextAlign("right").run()}
                            isActive={editor.isActive({ textAlign: "right" })}
                        >
                            <i className="ti ti-align-right" />
                        </ToolbarBtn>

                        <div className="separator" />

                        {/* Undo / Redo */}
                        <ToolbarBtn
                            title="Undo"
                            onClick={() => editor.chain().focus().undo().run()}
                            disabled={!editor.can().undo()}
                        >
                            <i className="ti ti-arrow-back-up" />
                        </ToolbarBtn>
                        <ToolbarBtn
                            title="Redo"
                            onClick={() => editor.chain().focus().redo().run()}
                            disabled={!editor.can().redo()}
                        >
                            <i className="ti ti-arrow-forward-up" />
                        </ToolbarBtn>

                        <div className="separator" />
                    </>
                )}

                {/* Insert Variable */}
                <Dropdown onToggle={handleVariableDropdownToggle}>
                    <Dropdown.Toggle
                        as="button"
                        type="button"
                        className="btn btn-sm btn-outline-primary border-0 px-2 py-1"
                        style={{ fontSize: 12 }}
                        id="insert-variable-dropdown"
                    >
                        <i className="ti ti-code me-1" />
                        Insert Variable
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: 300, overflowY: "auto", minWidth: 320 }}>
                        {variables.length === 0 ? (
                            <Dropdown.Item disabled>Loading...</Dropdown.Item>
                        ) : (
                            variables.map((v, idx) => (
                                <Dropdown.Item
                                    key={idx}
                                    onClick={() => insertVariable(v.variable)}
                                    title={v.description}
                                >
                                    <span className="rt-variable-chip me-2">{v.variable}</span>
                                    <small className="text-muted">{v.description}</small>
                                </Dropdown.Item>
                            ))
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                {/* Spacer + raw toggle */}
                <div style={{ marginLeft: "auto" }}>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{rawMode ? "Switch to visual editor" : "Edit raw HTML"}</Tooltip>}
                    >
                        <Button
                            variant={rawMode ? "secondary" : "outline-secondary"}
                            size="sm"
                            onClick={handleToggleRaw}
                            style={{ fontSize: 11, padding: "2px 8px" }}
                        >
                            <i className="ti ti-code me-1" />
                            {rawMode ? "Visual" : "HTML"}
                        </Button>
                    </OverlayTrigger>
                </div>
            </div>

            {/* Editor body */}
            {rawMode ? (
                <textarea
                    className="rt-editor-content form-control border-0 rounded-0"
                    style={{
                        fontFamily: "monospace",
                        fontSize: "0.82rem",
                        minHeight: 300,
                        resize: "vertical",
                    }}
                    value={rawValue}
                    onChange={handleRawChange}
                />
            ) : (
                <div className="rt-editor-content">
                    <EditorContent editor={editor} />
                </div>
            )}
        </div>
    );
};

export default RichTextEditor;
